import { useCallback, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import SecurityValidationHeader from "./SecurityValidationHeader";
import SecurityValidationBody from "./SecurityValidationBody";
import SecurityValidationFooter from "./SecurityValidationFooter";
import useExpirationTimer from "./useExpirationTimer";
import "./SecurityValidationModal.css";

const SecurityValidationModal = ({
  visible,
  onHide,
  email,
  amountLabel,
  recipientName,
  validating,
  resending,
  onValidate,
  onResend,
  confirmTemplateKey,
  codeLength = 4,
  codeTtlMs,
}) => {
  const [code, setCode] = useState("");
  const { formattedTime, isExpired, restart } = useExpirationTimer(visible, codeTtlMs);

  useEffect(() => {
    if (!visible) {
      setCode("");
      return;
    }
    restart();
  }, [visible, restart]);

  const handleHide = useCallback(() => {
    if (validating) return;
    onHide();
  }, [validating, onHide]);

  const handleResend = useCallback(async () => {
    if (!isExpired || resending || validating) return;
    const ok = await onResend();
    if (ok) {
      setCode("");
      restart();
    }
  }, [isExpired, resending, validating, onResend, restart]);

  const canValidate = String(code).length === codeLength && !isExpired && !resending;

  const handleValidate = () => {
    if (!canValidate || validating) return;
    onValidate(code);
  };

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={null}
      className="security-validation-dialog"
      headerClassName="hidden"
      modal
      dismissableMask={!validating}
      closable={false}
      draggable={false}
      style={{ width: "min(100vw - 2rem, 32rem)" }}
    >
      <SecurityValidationHeader onClose={handleHide} disabled={validating} />
      <SecurityValidationBody
        email={email}
        amountLabel={amountLabel}
        recipientName={recipientName}
        code={code}
        onCodeChange={setCode}
        formattedTime={formattedTime}
        isExpired={isExpired}
        resending={resending}
        validating={validating}
        onResend={handleResend}
        confirmTemplateKey={confirmTemplateKey}
        codeLength={codeLength}
      />
      <SecurityValidationFooter
        onCancel={handleHide}
        onValidate={handleValidate}
        canValidate={canValidate}
        validating={validating}
        disabled={validating}
      />
    </Dialog>
  );
};

export default SecurityValidationModal;
